/**
 * §P.9 fase 2 · Bajas y motivos.
 *
 * ## `avg_months_enrolled` es lo que convierte esto en una decisión
 *
 * «Perder alumnos a los dos meses es un problema de acogida; a los dos años es la
 * vida normal de un alumno.» Sin esa columna, el informe solo da malas noticias; con
 * ella dice **cuál** es el problema, y si hay que cambiar la primera clase o no
 * cambiar nada.
 *
 * Así que la pantalla no ordena por número de bajas: marca las tempranas, que son
 * las únicas que llevan a hacer algo distinto mañana.
 *
 * ## El motivo es texto libre escrito por una persona
 *
 * Con sus comas dentro —«se muda, dice»—, y por eso el CSV las escapa. Aquí se
 * enseña tal cual: resumirlo en categorías perdería justo lo que el maestro escribió.
 */
import { api } from '@/shared/api/client';
import { ErrorDeApi, aProblema } from '@/shared/api/errores';
import { EnvolturaDeInforme } from '@/club/informes/Envoltura';
import {
  BAJA_TEMPRANA_MESES,
  esBajaTemprana,
  etiquetaDeMotivoDeBaja,
  mesEnPalabras,
  type FilaDeBajas,
  type Informe,
} from '@/club/informes/tipos';

export function Bajas({ club }: { club: string }) {
  return (
    <EnvolturaDeInforme<FilaDeBajas>
      club={club}
      clave="bajas"
      ruta="retention"
      titulo="Bajas"
      explicacion={
        <>
          Quién se ha ido, cuándo y por qué. Lo que hay que mirar no es el
          número sino <strong>cuánto tiempo llevaban</strong>: irse a los dos
          meses y irse a los dos años son dos problemas distintos, y uno de
          ellos no es un problema.
        </>
      }
      vacio={{
        titulo: 'No se ha ido nadie',
        explicacion:
          'Cuando algún alumno se dé de baja aparecerá aquí, con su motivo y con cuánto tiempo estuvo en el club.',
      }}
      pedir={async () => {
        const { data, error } = await api.v1
          .clubs({ club })
          .reports.retention.get({ query: {} });
        if (error) throw new ErrorDeApi(aProblema(error));
        return data as unknown as Informe<FilaDeBajas>;
      }}
    >
      {(datos) => {
        const tempranas = datos.rows.filter(esBajaTemprana);
        const total = datos.totals['students'] ?? 0;

        return (
          <>
            {/*
              El aviso que da sentido al informe: si se van pronto, el problema está
              en la acogida y no en las clases.
            */}
            {tempranas.length > 0 && (
              <div className="aviso" role="status">
                <strong>Hay bajas que se fueron antes de engancharse.</strong>{' '}
                Con menos de {BAJA_TEMPRANA_MESES} meses de media, lo que falla
                suele ser la acogida —las primeras clases, el trato del primer
                mes— y no el entrenamiento. Es la parte que se puede cambiar
                esta semana.
              </div>
            )}

            <dl className="datos">
              <div>
                <dt>Bajas en el periodo</dt>
                <dd>{total}</dd>
              </div>
              <div>
                <dt>Motivos distintos</dt>
                <dd>{new Set(datos.rows.map((f) => f.leave_reason)).size}</dd>
              </div>
            </dl>

            <div className="tabla-envoltura">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>Mes</th>
                    <th>Motivo</th>
                    <th className="num">Alumnos</th>
                    <th className="num">Meses de media</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.rows.map((f, i) => (
                    <tr key={`${f.month}-${f.leave_reason}-${i}`}>
                      <td>{mesEnPalabras(f.month)}</td>
                      <td>{etiquetaDeMotivoDeBaja(f.leave_reason)}</td>
                      <td className="num">{f.students}</td>
                      <td className="num">
                        {f.avg_months_enrolled === null ? (
                          '—'
                        ) : esBajaTemprana(f) ? (
                          /* Marcada, no ordenada: es la lectura que importa. */
                          <span className="pastilla pastilla-mal">
                            {f.avg_months_enrolled}
                          </span>
                        ) : (
                          f.avg_months_enrolled
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        );
      }}
    </EnvolturaDeInforme>
  );
}
